import './external-tool.css';

import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { SWRConfig, SWRConfiguration } from 'swr';
import { ThemeProvider, Typography } from '@mui/material';

import AppToast from '../AppToast';
import AuthService from '../shared/services/AuthService';
import Button from '@mui/material/Button';
import { CustomLoadingProgress } from '../shared/components/CustomLoadingProgress';
import ExternalLinkLesson from './ExternalLinkLesson';
import ExternalToolLesson from './ExternalToolLesson';
import HtmlPageService from '../shared/services/HtmlPageService';
import MembershipService from '../membership/services/MembershipService';
import { SkinThemeProvider } from '../shared/SkinThemeContext';
import SkinThemeService from '../shared/services/SkinThemeService';
import SplashScreen from '../app/components/SplashScreen';
import User from '../membership/models/User';
import axios from 'axios';
import useThemes from '../shared/hooks/useThemes';

/** Path roots for public routes. */
const PUBLIC_USER: User = {
  firstname: 'Guest',
  lastname: 'User',
  friendlyname: 'Guest User'
} as User;

const PUBLIC_ROUTES = {
  '/link/lesson/:linkkey': ExternalLinkLesson
};
const ROUTES: { [route: string]: (props: any) => JSX.Element } = {
  ...PUBLIC_ROUTES,
  '/lesson/:lessonkey': ExternalToolLesson
};

/**
 * Returns a value indicating whether the path matches any public route (up to
 * the first :parameter).
 */
const isPublicRoute = (pathname: string): boolean =>
  Object.keys(PUBLIC_ROUTES).some((r) =>
    // `react-router-dom` -> `v6` changes the ROUTE. So, pathname is
    // `/e/link/lesson/:linkkey` and route is `/link/lesson/:linkkey`
    pathname.startsWith(r.substring(0, r.indexOf(':') || r.length), 2)
  );

/**
 * Interface for components nested within the external tool page.
 */
export interface IExternalToolComponentProps {
  /** Event for child component to signal a loading message. */
  onContentLoadingMessage?: (message: string | null) => void;
  /** Event for child component to signal an error message. */
  onContentErrorMessage?: (error: string | null) => void;
  /** Event to close or log out of the page. */
  onContentExit?: () => void;
}

/**
 * Component that renders Bracken as an external tool, perhaps an LTI lesson or
 * an external video analysis.
 */
const ExternalTool = () => {
  /** Path to page to match for public routes. */
  const { pathname } = useLocation();
  /** Value indicating whether route is public, rather than private. */
  const [isPublic, setIsPublic] = useState(isPublicRoute(pathname));
  /** Value indicating whether the current user is being authenticated. */
  const [loadingUser, setLoadingUser] = useState(!isPublic);
  /** Authenticated user. */
  const [currentUser, setCurrentUser] = useState<User>();
  /** Value indicating whether the user is being logged out. */
  const [loggingOut, setLoggingOut] = useState(false);
  /** Content to be displayed while inner components are loading. */
  const [contentLoadingMessage, setContentLoadingMessage] =
    useState<string>(null);
  /** Content to be displayed if inner component reports an error. */
  const [contentErrorMessage, setContentErrorMessage] = useState<string>(null);

  useEffect(() => {
    setIsPublic(isPublicRoute(pathname));
  }, [pathname]);

  /**
   * Load the user information from identity.
   */
  useEffect(() => {
    if (isPublic) {
      setLoadingUser(false);
      setCurrentUser(PUBLIC_USER);
      return;
    }
    setLoadingUser(true);
    AuthService.isAuthenticatedAsync()
      .then(async (oidcUser) => {
        if (!oidcUser) {
          throw `Missing OIDC user.`;
        }
        const userkey = +oidcUser.profile.sub;
        const user = await MembershipService.getUserByKeyAsync(userkey);
        if (!user) {
          throw `User ${userkey} membership not found.`;
        }
        setCurrentUser(user);
        AuthService.BrackenUser = user;
        setLoadingUser(false);
      })
      .catch((_) => {
        // Sometimes the authentication doesn't go through the first time. We go
        // to the login page here, normally the authentication will happen auto-
        // matically. The user won't be able to log in on the login page anyway.
        AuthService.login();
      });
  }, [pathname]);

  const { muiTheme, skinTheme } = useThemes();

  useEffect(() => {
    if (skinTheme) {
      HtmlPageService.setFavicon(
        SkinThemeService.getAssetUrl('container', 'favicon')
      );
      HtmlPageService.setTitle(
        SkinThemeService.getThemeOptionValue(
          skinTheme,
          'container',
          'siteName'
        ) as string
      );
    }
  }, [skinTheme]);

  /** Configuration for SWR cache loading */
  const swrConfig: SWRConfiguration = {
    fetcher: (url: string) =>
      axios(url, {
        headers: {
          Authorization: `Bearer ${AuthService.getAccessToken()}`
        }
      })
  };

  /**
   * Handle a request by an inner component to "exit".
   * We can't close the window, because we need to let the page redirect to log
   * out of the identity server.
   */
  const onContentExit = async () => {
    setLoggingOut(true);
    await AuthService.logout();
  };

  /** View properties. */
  const view = {
    /** Value indicating whether to show the splash screen. */
    showSplash: loadingUser || loggingOut || contentLoadingMessage,
    /** Value indicating whether to show a loading or message panel. */
    showPanel:
      loadingUser ||
      loggingOut ||
      !currentUser ||
      contentLoadingMessage ||
      contentErrorMessage,
    /** CSS class to apply to message panel. */
    className:
      loadingUser || loggingOut || contentLoadingMessage ? 'loading' : 'fill',
    /** Value indicating whether to show a loading spinner. */
    showProgress: loadingUser || loggingOut || contentLoadingMessage,
    /** Value indicating whether to show child component(s). */
    showContent: muiTheme && !loadingUser && !loggingOut && currentUser
  };

  return (
    <>
      {view.showSplash && <SplashScreen />}
      {view.showPanel && (
        <div className={`external-tool ${view.className}`}>
          <div className="content">
            {view.showProgress && (
              <>
                <CustomLoadingProgress size={24} />
                {loadingUser && <p>Starting...</p>}
                {loggingOut && <p>Logging out...</p>}
                {contentLoadingMessage && <p>{contentLoadingMessage}</p>}
              </>
            )}
            {!loadingUser && !currentUser && !isPublic && (
              <>
                <Typography variant="body1">
                  Please log in to see this content.
                </Typography>
                <p>
                  <Button
                    onClick={() => AuthService.login()}
                    color="primary"
                    variant="contained"
                  >
                    <Typography>Log in</Typography>
                  </Button>
                </p>
              </>
            )}
            {!loadingUser && contentErrorMessage && (
              <Typography variant="body1">{contentErrorMessage}</Typography>
            )}
          </div>
        </div>
      )}
      {view.showContent && (
        <ThemeProvider theme={muiTheme}>
          <SkinThemeProvider value={skinTheme}>
            <SWRConfig value={swrConfig}>
              <Routes>
                {Object.entries(ROUTES).map(([route, Component], index) => (
                  <Route
                    key={index}
                    path={route}
                    element={
                      <Component
                        onContentLoadingMessage={setContentLoadingMessage}
                        onContentErrorMessage={setContentErrorMessage}
                        onContentExit={onContentExit}
                      />
                    }
                  />
                ))}
              </Routes>
            </SWRConfig>
            <AppToast />
          </SkinThemeProvider>
        </ThemeProvider>
      )}
    </>
  );
};

export default ExternalTool;
